import { html } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map';

import { SimpleLitElement } from '@elab/components';
import { myAccountUrls } from '@elab/core/utils/constants';
import connect from '@elab/core/store/connect';
import { actions as uiActions } from '@elab/core/store/ui';
import { actions as globalActions } from '@elab/core/store/global';
import analytics from '@elab/core/utils/analytics';
import { isExternalURL } from '@elab/core/utils/url';

/**
 * Component is used to render a single link of the My Account navigation.
 * It is visible only on desktop (as an anchor) and writes it's data to 'ui.accountNav' store.
 *
 * @author Roberto Schiavone
 * @attr {String} [url] - the url to follow after the link is clicked
 * @attr {String} [title]
 * @attr {String} [gtm-event] - 'event' parameter of the tracked event pushed to the dataLayer
 * @attr {String} [gtm-label] - 'eventLabel' parameter of the tracked event pushed to the dataLayer
 * @attr {String} [class-modifier] - custom class modifier
 * HTML Structure
 * <e2-my-account-navigation-link url="/my-account-navigation.html"
 *                                title="Your Overview"></e2-my-account-navigation-link
 *                                class-modifier="vip-personalization">
 * */

class MyAccountNavigationLink extends SimpleLitElement {
    static get properties() {
        return {
            url: { type: String },
            title: { type: String },
            hasActiveNestedPage: { type: Boolean },
            gtmEvent: { type: String, attribute: 'gtm-event' },
            gtmLabel: { type: String, attribute: 'gtm-label' },
            classModifier: {
                type: String,
                attribute: 'class-modifier',
            },
            iconClassName: {
                type: String,
                attribute: 'icon-class-name',
            },
            active: { type: Boolean, attribute: false },
            isPrimaryLink: { type: Boolean, attribute: false },
            checkIfEndsWithUrl: { type: Boolean, attribute: 'check-if-ends-with-url' },
        };
    }

    constructor() {
        super();

        this.accordion = null;
        this.hasActiveNestedPage = false;
        this.active = false;
        this.isPrimaryLink = true;
        this.classModifier = '';
        this.checkIfEndsWithUrl = false;
    }

    handleClick(e) {
        e.preventDefault();

        if (this.gtmEvent) {
            analytics.handleClick(this.gtmEvent, this.gtmLabel);
        }

        if (isExternalURL(this.url)) {
            analytics.handleOutboundLinks(this.url);
        }

        if (this.url) {
            this.dispatchEvent(new CustomEvent('redirect', { detail: this.url }));
        }
    }

    render() {
        return html`
            ${this.isPrimaryLink ? this.primaryLinkTemplate : this.secondaryLinkTemplate}
        `;
    }

    get primaryLinkTemplate() {
        return html`
            <a
                class="my-account-navigation__link my-account-navigation__link--primary ${this
                    .renderLinkActiveClass}"
                href="${this.url}"
                title="${this.title}"
                @click="${this.handleClick}"
            >
                <span class="my-account-navigation__link-icon">
                    <i class="${this.iconClassName}"></i>
                </span>
                <h5
                    class="my-account-navigation__title ${this.active
                        ? 'my-account-navigation__title--active'
                        : ''}"
                >
                    ${this.title}
                </h5>
                <i
                    class="my-account-navigation__arrow my-account-navigation__arrow--right
                        ${this.active ? 'my-account-navigation__arrow--active' : ''}"
                ></i>
            </a>
        `;
    }

    get secondaryLinkTemplate() {
        return html`
            <a
                class="my-account-navigation__link my-account-navigation__link--secondary ${this
                    .renderLinkActiveClass}"
                href="${this.url}"
                title="${this.title}"
                @click="${this.handleClick}"
            >
                ${this.title}
            </a>
        `;
    }

    get renderLinkActiveClass() {
        return classMap({
            'my-account-navigation__link--active': this.active,
            [`my-account-navigation__link--${this.classModifier}`]: !!this.classModifier,
        });
    }

    getParent(element, parentName) {
        const result = element.parentNode;
        return result && result.nodeName.toLowerCase() !== parentName
            ? this.getParent(result, parentName)
            : result;
    }

    activateLink() {
        const url = this.url.toLowerCase();
        const currentPage = location.pathname.toLowerCase();
        if (
            url === currentPage ||
            (currentPage.includes(myAccountUrls.myAccountOrder) &&
                url === myAccountUrls.myAccountOrders) ||
            this.hasActiveNestedPage
        ) {
            this.active = true;
        }
    }

    openAccordion() {
        const accordion = this.getParent(this, 'e2-my-account-navigation-group');
        if (accordion) {
            this.accordion = accordion;
            this.isPrimaryLink = false;
            if (this.active) {
                accordion.open = true;
            }
        }
    }

    firstUpdated() {
        this.activateLink();
        this.openAccordion();

        // TODO: legacy KV code, mobile my account menu logic need to be refactored
        setTimeout(this.onComponentReady.bind(this), 0);

        if (this.gtmEvent) {
            analytics.handleView({
                event: this.gtmEvent,
                label: this.gtmLabel,
            });
        }
    }

    onComponentReady() {
        if (this.isPrimaryLink) {
            this.dispatchEvent(
                new CustomEvent('account-navigation-add', {
                    detail: { title: this.title, url: this.url },
                }),
            );
        }
        if (this.active) {
            this.dispatchEvent(
                new CustomEvent('account-navigation-set-active', {
                    detail: { title: this.title, url: this.url },
                }),
            );
        }
    }
}

const mapDispatchToEvents = dispatch => ({
    'account-navigation-add': ({ detail }) => dispatch(uiActions.addAccountNavigation(detail)),
    'account-navigation-set-active': ({ detail }) =>
        dispatch(uiActions.setAccountNavigationActive(detail)),
    redirect: e =>
        dispatch(
            globalActions.redirect({
                successRedirectUrl: e.detail,
                checkContentType: true,
            }),
        ),
});

export default () =>
    customElements.define(
        'e2-my-account-navigation-link',
        connect(null, mapDispatchToEvents)(MyAccountNavigationLink),
    );
